import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bookmark,
  Box,
  Briefcase,
  Building,
  Calendar,
  Car,
  ClipboardList,
  Clock,
  Folder,
  Gift,
  IdCard,
  LayoutDashboard,
  LayoutGrid,
  List,
  LocateFixed,
  MapPin,
  Megaphone,
  Menu,
  Percent,
  Settings,
  Shield,
  Star,
  Tag,
  TrendingDown,
  TrendingUp,
  User,
  Wallet,
  Wrench,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  useLocation,
} from 'react-router';
import { MobileNavDrawer } from './components/ui/MobileNavDrawer.js';
import { PrivateRoute } from './components/ui/PrivateRoute.js';
import { SidebarFooter } from './components/ui/SidebarFooter.js';
import { type NavItem, SidebarNav } from './components/ui/SidebarNav.js';
import { AbilityProvider } from './context/AbilityContext.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { ForcePasswordChangePage } from './pages/auth/ForcePasswordChangePage.js';
import { LoginPage } from './pages/auth/LoginPage.js';
import { BookingCreatePage } from './pages/bookings/BookingCreatePage.js';
import { BookingDetailPage } from './pages/bookings/BookingDetailPage.js';
import { BookingListPage } from './pages/bookings/BookingListPage.js';
import { CarBrandFormPage } from './pages/carBrands/CarBrandFormPage.js';
import { CarBrandListPage } from './pages/carBrands/CarBrandListPage.js';
import { CarCategoryFormPage } from './pages/carCategories/CarCategoryFormPage.js';
import { CarCategoryListPage } from './pages/carCategories/CarCategoryListPage.js';
import { CarFeatureFormPage } from './pages/carFeatures/CarFeatureFormPage.js';
import { CarFeatureListPage } from './pages/carFeatures/CarFeatureListPage.js';
import { CarModelFormPage } from './pages/carModels/CarModelFormPage.js';
import { CarModelListPage } from './pages/carModels/CarModelListPage.js';
import { CarFormPage } from './pages/cars/CarFormPage.js';
import { CarListPage } from './pages/cars/CarListPage.js';
import { CartrackPage } from './pages/cartrack/CartrackPage.js';
import { DashboardPage } from './pages/dashboard/DashboardPage.js';
import { DemandMissPage } from './pages/demand/DemandMissPage.js';
import { DiscountCodeListPage } from './pages/discounts/DiscountCodeListPage.js';
import { DiscountFormPage } from './pages/discounts/DiscountFormPage.js';
import { DiscountListPage } from './pages/discounts/DiscountListPage.js';
import { FinancePage } from './pages/finance/FinancePage.js';
import { FineFormPage } from './pages/fines/FineFormPage.js';
import { FineListPage } from './pages/fines/FineListPage.js';
import { GiftCertificateFormPage } from './pages/gift-certificates/GiftCertificateFormPage.js';
import { GiftCertificateListPage } from './pages/gift-certificates/GiftCertificateListPage.js';
import { LocationFormPage } from './pages/locations/LocationFormPage.js';
import { LocationListPage } from './pages/locations/LocationListPage.js';
import { MaintenanceFormPage } from './pages/maintenance/MaintenanceFormPage.js';
import { MaintenanceListPage } from './pages/maintenance/MaintenanceListPage.js';
import { MembershipDetailPage } from './pages/memberships/MembershipDetailPage.js';
import { MembershipListPage } from './pages/memberships/MembershipListPage.js';
import { PenaltyDashboardPage } from './pages/penalties/PenaltyDashboardPage.js';
import { PenaltyListPage } from './pages/penalties/PenaltyListPage.js';
import { PricingTemplateFormPage } from './pages/pricing/PricingTemplateFormPage.js';
import { PricingTemplateListPage } from './pages/pricing/PricingTemplateListPage.js';
import { ProfilePage } from './pages/profile/ProfilePage.js';
import { PromoDashboardPage } from './pages/promotions/PromoDashboardPage.js';
import { RevenueByCarPage } from './pages/revenue/RevenueByCarPage.js';
import { ReviewDetailPage } from './pages/reviews/ReviewDetailPage.js';
import { ReviewListPage } from './pages/reviews/ReviewListPage.js';
import { RoleCreatePage } from './pages/roles/RoleCreatePage.js';
import { RoleDetailPage } from './pages/roles/RoleDetailPage.js';
import { RolesListPage } from './pages/roles/RolesListPage.js';
import { SalesStatementPage } from './pages/sales/SalesStatementPage.js';
import { SchedulePage } from './pages/schedule/SchedulePage.js';
import { SettingsPage } from './pages/settings/SettingsPage.js';
import { SupplierCreatePage } from './pages/suppliers/SupplierCreatePage.js';
import { SupplierListPage } from './pages/suppliers/SupplierListPage.js';
import { SupplierProfilePage } from './pages/suppliers/SupplierProfilePage.js';
import { UserCreatePage } from './pages/users/UserCreatePage.js';
import { UserDetailPage } from './pages/users/UserDetailPage.js';
import { UserListPage } from './pages/users/UserListPage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

/**
 * 063 (gap-list H11): every leaf declares what it needs of its caller.
 *
 * The `NavItem[]` annotation is LOAD-BEARING, not decoration. Without it
 * TypeScript infers the literal's own shape and a missing `need` is simply
 * absent from the inferred type — no error. With it, an omission fails the
 * build, which is the whole of FR-002 (proved by deletion; see VERIFICATION.md).
 *
 * A list destination declares `read`. The four taxonomy screens declare
 * `manage` deliberately: a provider's `read CarBrand` exists to fill the car
 * form, not to administer the global catalogue, and this feature removes lies
 * rather than widening the panel.
 */
// Exported so the gating tests assert the SHIPPED declarations rather than a
// copy of them — a copied table drifts, and a drifted table would pass while
// the panel misleads.
export const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', need: 'always', icon: <LayoutGrid className="h-4 w-4" /> },
  {
    label: 'Car',
    icon: <Car className="h-4 w-4" />,
    children: [
      // Taxonomy — administration screens, so `manage`, not `read`.
      {
        to: '/car-brands',
        label: 'Brands',
        need: ['manage', 'CarBrand'],
        icon: <Bookmark className="h-4 w-4" />,
      },
      {
        to: '/car-categories',
        label: 'Categories',
        need: ['manage', 'CarCategory'],
        icon: <Folder className="h-4 w-4" />,
      },
      {
        to: '/car-models',
        label: 'Models',
        need: ['manage', 'CarModel'],
        icon: <Box className="h-4 w-4" />,
      },
      {
        to: '/car-features',
        label: 'Features',
        need: ['manage', 'CarFeature'],
        icon: <Star className="h-4 w-4" />,
      },
      // Fleet + operational. A host holds no Fine rule, so Fines hides for them.
      { to: '/cars', label: 'Fleet', need: ['read', 'Car'], icon: <List className="h-4 w-4" /> },
      // 405 (#315): the cars' Cartrack links and the plate match. Platform administrators only, never a type-level
      // pair (a host's scoped car grant passes `can`).
      {
        to: '/cartrack',
        label: 'Cartrack',
        need: { anywhere: [['manage', 'Car']] },
        icon: <LocateFixed className="h-4 w-4" />,
      },
      {
        to: '/maintenance',
        label: 'Maintenance',
        need: ['read', 'MaintenanceRecord'],
        icon: <Wrench className="h-4 w-4" />,
      },
      {
        to: '/fines',
        label: 'Fines',
        need: ['read', 'Fine'],
        icon: <AlertTriangle className="h-4 w-4" />,
      },
    ],
  },
  {
    to: '/locations',
    label: 'Locations',
    need: ['read', 'Location'],
    icon: <MapPin className="h-4 w-4" />,
  },
  {
    to: '/pricing',
    label: 'Pricing',
    need: ['read', 'PricingTemplate'],
    icon: <Tag className="h-4 w-4" />,
  },
  {
    label: 'Bookings',
    icon: <Calendar className="h-4 w-4" />,
    children: [
      {
        to: '/bookings',
        label: 'Bookings',
        need: ['read', 'Booking'],
        icon: <ClipboardList className="h-4 w-4" />,
      },
      // 094: gated on CarBlock, the subject GET /schedule refuses on — a caller who may
      // read bookings but not blocks would be shown a fleet drawn entirely free.
      {
        to: '/schedule',
        label: 'Schedule',
        need: ['read', 'CarBlock'],
        icon: <Clock className="h-4 w-4" />,
      },
      // 371 (#1241): customer penalties. 412: for staff who may read them platform-wide (manage covers it).
      {
        to: '/penalties',
        label: 'Penalties',
        need: { anywhere: [['read', 'Penalty']] },
        end: true,
        icon: <AlertTriangle className="h-4 w-4" />,
      },
      // 397 (#1328): the penalties added up, per car and per supplier; same rule as the list.
      {
        to: '/penalties/dashboard',
        label: 'Penalty dashboard',
        need: { anywhere: [['read', 'Penalty']] },
        icon: <LayoutDashboard className="h-4 w-4" />,
      },
      {
        to: '/reviews',
        label: 'Reviews',
        need: ['read', 'Review'],
        icon: <Star className="h-4 w-4" />,
      },
      // 214 (#310): "how much demand did we turn away" — booking attempts refused for lack of
      // availability, across the whole platform, not scoped to a single booking.
      {
        to: '/demand-misses',
        label: 'Turned-Away Demand',
        need: ['read', 'DemandMiss'],
        icon: <TrendingDown className="h-4 w-4" />,
      },
      // #450: same `manage Booking` gate as the dashboard's own revenue figure — platform-wide,
      // admin/superadmin only.
      {
        to: '/revenue-by-car',
        label: 'Revenue by Car',
        need: ['manage', 'Booking'],
        icon: <TrendingUp className="h-4 w-4" />,
      },
      // 342 (#933): a host's sales statement. A supplier's staff read their own organisation's; platform staff any.
      {
        to: '/sales-statement',
        label: 'Sales Statement',
        need: ['read', 'SalesStatement'],
        icon: <TrendingUp className="h-4 w-4" />,
      },
      // 351 (#948): every payment and refund on the platform — platform-wide permission only.
      {
        to: '/finance',
        label: 'Finance',
        need: {
          anywhere: [
            ['read', 'Payment'],
            ['read', 'Refund'],
          ],
        },
        icon: <Wallet className="h-4 w-4" />,
      },
    ],
  },
  {
    label: 'Promotions',
    icon: <Megaphone className="h-4 w-4" />,
    children: [
      // 375 (#1239): what was given away, platform-wide (both permissions, platform-wide).
      {
        to: '/promotions/dashboard',
        label: 'Dashboard',
        need: {
          allAnywhere: [
            ['read', 'GiftCertificate'],
            ['read', 'Discount'],
          ],
        },
        icon: <LayoutDashboard className="h-4 w-4" />,
      },
      {
        to: '/discounts',
        label: 'Discounts',
        need: ['read', 'Discount'],
        icon: <Percent className="h-4 w-4" />,
      },
      {
        to: '/gift-certificates',
        label: 'Gift Certificates',
        need: ['read', 'GiftCertificate'],
        icon: <Gift className="h-4 w-4" />,
      },
    ],
  },
  { to: '/users', label: 'Users', need: ['read', 'User'], icon: <User className="h-4 w-4" /> },
  // 207 (#313): Admin gets read Role (view all 5, including SuperAdmin/Customer's protected read-
  // only entries); Supplier/Host/Customer hold no Role-subject grant at all, so this correctly
  // stays hidden from them.
  { to: '/roles', label: 'Roles', need: ['read', 'Role'], icon: <Shield className="h-4 w-4" /> },
  {
    label: 'Supplier',
    icon: <Briefcase className="h-4 w-4" />,
    children: [
      {
        to: '/suppliers',
        label: 'Suppliers',
        need: ['read', 'Supplier'],
        icon: <Building className="h-4 w-4" />,
      },
      // 046: employee roster. Providers hold a scoped read and the page already
      // renders read-only for them, so it is offered rather than URL-only (FR-017).
      {
        to: '/memberships',
        label: 'Memberships',
        need: ['read', 'Membership'],
        icon: <IdCard className="h-4 w-4" />,
      },
    ],
  },
  {
    to: '/settings',
    label: 'Settings',
    need: ['read', 'PlatformConfig'],
    icon: <Settings className="h-4 w-4" />,
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: close on route change only
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Close the drawer when the viewport reaches the desktop breakpoint. The
  // drawer is hidden at lg via `lg:hidden` (pure CSS), but its open state must
  // reset too — otherwise dropping back below lg re-reveals a drawer the user
  // never reopened (FR-003d). matchMedia fires only on breakpoint crossings,
  // not on every resize.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  function closeDrawer() {
    setDrawerOpen(false);
    hamburgerRef.current?.focus();
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface gap-4 p-4">
      <button
        ref={hamburgerRef}
        type="button"
        aria-label="Menu"
        className="lg:hidden fixed top-4 left-4 z-40 rounded-md bg-canvas p-2 shadow"
        onClick={() => setDrawerOpen(true)}
      >
        <Menu size={20} aria-hidden="true" />
      </button>
      <MobileNavDrawer open={drawerOpen} onClose={closeDrawer} navItems={navItems} />
      <aside className="hidden lg:flex w-56 shrink-0 flex-col rounded-xl shadow-[0px_4px_12px_0px_rgba(0,30,43,0.08)] overflow-hidden bg-canvas">
        <div className="flex items-center justify-between px-6 py-5 border-b border-hairline-soft">
          <div>
            <span className="text-lg font-bold text-primary">CARii Kereta</span>
            <span className="ml-1 text-xs text-steel">Admin</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <SidebarNav items={navItems} />
        </div>
        <SidebarFooter />
      </aside>
      <main className="flex-1 overflow-y-auto rounded-xl shadow-[0px_4px_12px_0px_rgba(0,30,43,0.08)] bg-canvas p-8 pt-16 lg:pt-8">
        {children}
      </main>
    </div>
  );
}

function AuthenticatedApp() {
  const { ability } = useAuth();
  return (
    <AbilityProvider ability={ability}>
      <Layout>
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="cars" element={<CarListPage />} />
          <Route path="cars/new" element={<CarFormPage />} />
          <Route path="cars/:id/edit" element={<CarFormPage />} />
          <Route path="cartrack" element={<CartrackPage />} />
          {/* 043: CAR taxonomy CRUD (admin-only via CASL gating on the pages/nav) */}
          <Route path="car-brands" element={<CarBrandListPage />} />
          <Route path="car-brands/new" element={<CarBrandFormPage />} />
          <Route path="car-brands/:id/edit" element={<CarBrandFormPage />} />
          <Route path="car-categories" element={<CarCategoryListPage />} />
          <Route path="car-categories/new" element={<CarCategoryFormPage />} />
          <Route path="car-categories/:id/edit" element={<CarCategoryFormPage />} />
          <Route path="car-models" element={<CarModelListPage />} />
          <Route path="car-models/new" element={<CarModelFormPage />} />
          <Route path="car-models/:id/edit" element={<CarModelFormPage />} />
          <Route path="car-features" element={<CarFeatureListPage />} />
          <Route path="car-features/new" element={<CarFeatureFormPage />} />
          <Route path="car-features/:id/edit" element={<CarFeatureFormPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="maintenance" element={<MaintenanceListPage />} />
          <Route path="maintenance/new" element={<MaintenanceFormPage />} />
          <Route path="maintenance/:id/edit" element={<MaintenanceFormPage />} />
          <Route path="fines" element={<FineListPage />} />
          <Route path="fines/new" element={<FineFormPage />} />
          <Route path="fines/:id/edit" element={<FineFormPage />} />
          <Route path="locations" element={<LocationListPage />} />
          <Route path="locations/new" element={<LocationFormPage />} />
          <Route path="locations/:id/edit" element={<LocationFormPage />} />
          <Route path="pricing" element={<PricingTemplateListPage />} />
          <Route path="pricing/new" element={<PricingTemplateFormPage />} />
          <Route path="pricing/:id/edit" element={<PricingTemplateFormPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="bookings" element={<BookingListPage />} />
          <Route path="bookings/new" element={<BookingCreatePage />} />
          <Route path="bookings/:id" element={<BookingDetailPage />} />
          <Route path="reviews" element={<ReviewListPage />} />
          <Route path="reviews/:id" element={<ReviewDetailPage />} />
          <Route path="users" element={<UserListPage />} />
          <Route path="users/new" element={<UserCreatePage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="roles" element={<RolesListPage />} />
          <Route path="roles/new" element={<RoleCreatePage />} />
          <Route path="roles/:id" element={<RoleDetailPage />} />
          {/* 044: unified discounts surface (code + customer scopes). */}
          <Route path="discounts" element={<DiscountListPage />} />
          <Route path="discounts/new" element={<DiscountFormPage />} />
          <Route path="discounts/:id/edit" element={<DiscountFormPage />} />
          <Route path="discounts/:id/codes" element={<DiscountCodeListPage />} />
          {/* 214 (#310): turned-away demand — booking attempts that failed for lack of availability. */}
          <Route path="demand-misses" element={<DemandMissPage />} />
          {/* #450: per-vehicle monthly revenue, net of the platform fee. */}
          <Route path="revenue-by-car" element={<RevenueByCarPage />} />
          {/* 342 (#933): the host sales statement. */}
          <Route path="sales-statement" element={<SalesStatementPage />} />
          {/* 351 (#948): payments and refunds. */}
          <Route path="finance" element={<FinancePage />} />
          <Route path="promotions/dashboard" element={<PromoDashboardPage />} />
          <Route path="penalties" element={<PenaltyListPage />} />
          <Route path="penalties/dashboard" element={<PenaltyDashboardPage />} />
          <Route path="gift-certificates" element={<GiftCertificateListPage />} />
          <Route path="gift-certificates/new" element={<GiftCertificateFormPage />} />
          <Route path="gift-certificates/:id" element={<GiftCertificateFormPage />} />
          <Route path="memberships" element={<MembershipListPage />} />
          <Route path="memberships/:id" element={<MembershipDetailPage />} />
          <Route path="suppliers" element={<SupplierListPage />} />
          <Route path="suppliers/new" element={<SupplierCreatePage />} />
          <Route path="suppliers/:id" element={<SupplierProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </AbilityProvider>
  );
}

function RootRoutes() {
  // 035 R2: a force-flagged user (temp-password account) is blocked from the app
  // until they change their password — short-circuit every route to the
  // change-password screen. Placed above the login/private routes so it wins.
  const { mustChangePassword } = useAuth();
  if (mustChangePassword) return <ForcePasswordChangePage />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="*"
        element={
          <PrivateRoute>
            <AuthenticatedApp />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

/**
 * 356 (research R1): a data router, so a page can block navigation with `useBlocker` (Settings, with
 * unsaved changes). One catch-all route renders the same tree as before: every `<Routes>` below keeps
 * working as descendant routes, so no route definition changed.
 */
function createAppRouter() {
  return createBrowserRouter([
    {
      path: '*',
      element: (
        <AuthProvider>
          <RootRoutes />
        </AuthProvider>
      ),
    },
  ]);
}

export function App() {
  // Created on first render, not at import: modules that import `navItems` or `Layout` from here
  // (and mock the auth context) must not build a router as a side effect.
  const [router] = useState(createAppRouter);
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
